import { tmPelaFidelidade } from '../fidelidadeConta';

export type StatusAptidao = 'APTO' | 'APTO_COM_APARELHO' | 'MKT' | 'NAO_APTO' | 'PROSPECT' | 'INATIVO';

/**
 * REGRA ÚNICA da renovação — a mesma de fn_aptidao_renovacao no banco.
 * Douglas 25/09/2026:
 *  - sem tempo (carteira não tem o dado) → PROSPECT
 *  - M0–M11 → NÃO APTO, sempre
 *  - M12–M16 → APTO COM APARELHO (pelo menos um aparelho no pedido)
 *  - CG no M15 e M16 → APTO, sem aparelho (Douglas 26/09: o CG renova livre
 *    no M16 e a Prime fecha no M15, com o BKO esperando virar — igual ao M17)
 *  - M17 em diante → APTO
 * Na renovação parcial o TM é o das linhas renovadas (ver tmDaRenovacao).
 */
export function calcularAptidaoCarteira(
  tempoContratoVoz: number | null | undefined,
  classificacao?: string | null | undefined,
): StatusAptidao {
  if (tempoContratoVoz == null) return 'PROSPECT';
  const c = String(classificacao ?? '').trim().toUpperCase();
  if (tempoContratoVoz >= 17) return 'APTO';
  if (tempoContratoVoz >= 15 && c === 'CG') return 'APTO';
  if (tempoContratoVoz >= 12) return 'APTO_COM_APARELHO';
  return 'NAO_APTO';
}

export function renovacaoExigeAparelho(
  tempoContratoVoz: number | null | undefined,
  classificacao?: string | null | undefined,
): boolean {
  return calcularAptidaoCarteira(tempoContratoVoz, classificacao) === 'APTO_COM_APARELHO';
}

/** M12–M15 mantém o pacote de GB de cada linha; o CG fica livre já no M15. */
export function renovacaoMantemPacote(
  tempoContratoVoz: number | null | undefined,
  classificacao?: string | null | undefined,
): boolean {
  if (tempoContratoVoz == null) return false;
  const cg = String(classificacao ?? '').trim().toUpperCase() === 'CG';
  return tempoContratoVoz >= 12 && tempoContratoVoz <= 15 && !(cg && tempoContratoVoz === 15);
}

export type OrigemTmRenovacao = 'carteira' | 'fidelidade';

/**
 * O TM que decide aparelho e pacote na renovação (Douglas 26/09/2026: "quando
 * for parcial tem que deixar fazer pela fidelidade da linha"). Espelho de
 * fn_renovacao_tm_regra no banco:
 *  - renovação total → TM da carteira Claro (média do CNPJ)
 *  - parcial → o menor TM entre as linhas renovadas, pela fidelidade na conta
 *  - parcial com linha renovada sem fidelidade lida → TM da carteira
 * A comissão continua no TM da carteira.
 */
export function tmDaRenovacao(params: {
  tmCarteira: number | null | undefined;
  parcial: boolean;
  numerosRenovados: Array<string | null | undefined>;
  linhasConta: Array<{ numero?: string | null; dataFidelidade?: string | null }>;
  hoje?: Date;
}): { tm: number | null; origem: OrigemTmRenovacao } {
  const linha9 = (v: unknown) => String(v ?? '').replace(/\D/g, '').slice(-9);
  const renovadas = [...new Set(params.numerosRenovados.map(linha9).filter((n) => n.length === 9))];
  if (params.parcial && renovadas.length > 0) {
    const tms = renovadas.map((numero) => {
      const datas = params.linhasConta
        .filter((l) => linha9(l.numero) === numero && /^\d{4}-\d{2}-\d{2}/.test(String(l.dataFidelidade || '')))
        .map((l) => String(l.dataFidelidade).slice(0, 10))
        .sort();
      return datas.length ? tmPelaFidelidade(datas[datas.length - 1], params.hoje) : null;
    });
    if (tms.every((tm): tm is number => tm != null)) return { tm: Math.min(...tms), origem: 'fidelidade' };
  }
  return { tm: params.tmCarteira ?? null, origem: 'carteira' };
}

export const APTIDAO_LABELS: Record<StatusAptidao, string> = {
  APTO: 'Apto',
  APTO_COM_APARELHO: 'Apto c/ Aparelho',
  MKT: 'MKT',
  NAO_APTO: 'Não Apto',
  PROSPECT: 'Prospect',
  INATIVO: 'Inativo',
};
